import { get, set, del, setMany } from 'idb-keyval';

const STORE_KEY = 'solosync2_offline_logs';

/**
 * Retrieves the current array of offline logs from IndexedDB.
 * @returns {Promise<Array>} Array of pending logs
 */
export const getOfflineLogs = async () => {
    try {
        let logs = await get(STORE_KEY);
        if (typeof logs === 'string') {
            logs = JSON.parse(logs);
        }
        return logs || [];
    } catch (error) {
        console.error('Failed to get offline logs:', error);
        await del(STORE_KEY);
        return [];
    }
};

/**
 * Retrieves the current array, pushes the new payload, and saves it back.
 * @param {Object} logPayload The session data to save offline
 * @returns {Promise<void>}
 */
export const saveLogOffline = async (logPayload) => {
    try {
        const logs = await getOfflineLogs();
        logs.push(logPayload);
        await set(STORE_KEY, logs);
    } catch (error) {
        console.error('Failed to save log offline:', error);
        throw error; // Rethrow to let caller handle it if needed
    }
};

/**
 * Empties the array after a successful sync.
 * @returns {Promise<void>}
 */
export const clearOfflineLogs = async () => {
    try {
        await set(STORE_KEY, []);
    } catch (error) {
        console.error('Failed to clear offline logs:', error);
    }
};

/**
 * Syncs pending logs to the backend instance.
 * @param {Object} apiInstance The axios API instance
 * @returns {Promise<void>}
 */
export const syncPendingLogs = async (apiInstance) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
    }

    // Ensure the backend is reachable before attempting to sync
    try {
        await apiInstance.get('/api/solosync2/ping/');
    } catch (error) {
        console.log('Backend unreachable. Skipping sync for now.');
        return;
    }

    try {
        const logs = await getOfflineLogs();
        if (logs.length === 0) {
            return;
        }

        console.log(`Attempting to sync ${logs.length} offline log(s)...`);

        const remainingLogs = [];
        let syncErrors = 0;

        for (const log of logs) {
            try {
                if (log.is_challenge || (log.routine_id && log.score_numeric !== undefined)) {
                    await apiInstance.post('/api/solosync2/challenges/log/', log);
                } else {
                    await apiInstance.post('/api/solosync2/logs/', log);
                }
            } catch (error) {
                console.error('Failed to sync a log:', error);
                remainingLogs.push(log);
                syncErrors++;
            }
        }

        if (syncErrors > 0) {
            console.warn(`Failed to sync ${syncErrors} log(s). They will remain in offline storage.`);
        } else {
             console.log('Successfully synced all offline logs.');
        }
        
        // Update the store with any logs that failed to sync, or clear if empty
        await set(STORE_KEY, remainingLogs);

    } catch (error) {
        console.error('Error during sync process:', error);
    }
};

/**
 * Fetches the aggregated app data from the backend and saves it to IndexedDB.
 * @param {Object} apiInstance The axios API instance
 * @returns {Promise<boolean>} True if successful
 */
export const syncAppData = async (apiInstance) => {
    try {
        const response = await apiInstance.get('/api/solosync2/sync/');
        const data = response.data || {};
        const entries = [
            ['solosync2_master_data', data],
        ];

        const activeRoutines = data.active_routines || data.routines || [];
        const pastRoutines = data.past_routines || [];

        if (activeRoutines.length > 0) {
            entries.push(['cached_routines_list', activeRoutines]);
        }
        if (pastRoutines.length > 0) {
            entries.push(['cached_past_routines_list', pastRoutines]);
        }

        const allRoutines = [...activeRoutines, ...pastRoutines];
        allRoutines.forEach((routine) => {
            if (routine && routine.id) {
                entries.push([`offline_routine_${routine.id}`, routine]);
            }
        });

        await setMany(entries);
        console.log('App data successfully synced for offline use.');
        return true;
    } catch (error) {
        console.error('Failed to sync app data:', error);
        throw error;
    }
};

/**
 * Retrieves the cached master data from IndexedDB.
 * @returns {Promise<Object|null>} The cached data or null if not found
 */
export const getCachedAppData = async () => {
    try {
        let data = await get('solosync2_master_data');
        if (typeof data === 'string') {
            data = JSON.parse(data);
        }
        return data;
    } catch (error) {
        console.error('Failed to get cached app data:', error);
        await del('solosync2_master_data');
        return null;
    }
};

/**
 * Caches each routine individually into IndexedDB using the key format
 * `offline_routine_${routine.id}`. This ensures that SessionPlayerPage
 * can load any previously-viewed routine when offline.
 * Uses batched setMany for optimal main-thread performance.
 * @param {Array} routinesList Array of routine objects to cache
 * @returns {Promise<void>}
 */
export const cacheIndividualRoutines = async (routinesList) => {
    if (!Array.isArray(routinesList) || routinesList.length === 0) return;
    try {
        const validRoutines = routinesList.filter(r => r && r.id);
        if (validRoutines.length === 0) return;
        const entries = validRoutines.map((routine) => [
            `offline_routine_${routine.id}`,
            routine,
        ]);
        await setMany(entries);
    } catch (error) {
        console.error('Failed to cache individual routines:', error);
    }
};
